import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Flame, Quote, Target, Trophy, CalendarHeart } from 'lucide-react';
import { toast } from 'sonner';
import { fetchRecap } from '../lib/api';
import PageSkeleton from './nav/PageSkeleton';
import Breadcrumb from './nav/Breadcrumb';
import EmptyState from './nav/EmptyState';

/**
 * Pagina "Year in Review" / Recap annuale.
 *
 * Layout poster verticale (max-w-2xl) ottimizzato per screenshot/condivisione.
 *
 * Sezioni:
 *  - hero: anno + tema + perché
 *  - quote del coach (Claude)
 *  - 4 stat cards (giorni con MIT, tasso, streak record, MIT chiuse)
 *  - top 3 KR per progresso (con barra)
 *  - mese migliore
 *  - CTA verso heatmap dell'anno
 */
export default function PaginaRecap() {
  const { year: yearParam } = useParams();
  const navigate = useNavigate();
  const year = parseInt(yearParam, 10) || new Date().getFullYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchRecap(year)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'errore'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  const ratio = data?.mit?.ratio || 0;
  const ratioPct = Math.round(ratio * 100);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copiato');
    } catch {
      window.prompt('Copia il link:', window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-3xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo="/heatmap"
          backLabel="Heatmap"
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Heatmap', to: '/heatmap' },
            { label: `Recap ${year}` },
          ]}
        />
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-20" data-testid="recap-page">
        {/* Year nav */}
        <div className="flex items-center justify-between gap-2 mb-8">
          <button
            type="button"
            onClick={() => navigate(`/recap/${year - 1}`)}
            className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-soft hover:text-ink transition-colors"
            data-testid="recap-prev-year"
          >
            <ChevronLeft size={14} strokeWidth={1.5} /> {year - 1}
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute">Year in Review</span>
          <button
            type="button"
            onClick={() => navigate(`/recap/${year + 1}`)}
            className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-soft hover:text-ink transition-colors"
            data-testid="recap-next-year"
          >
            {year + 1} <ChevronRight size={14} strokeWidth={1.5} />
          </button>
        </div>

        {loading && (
          <PageSkeleton variant="grid" inline sections={3} testid="recap-loading" />
        )}
        {error && (
          <EmptyState
            icon={CalendarHeart}
            title="Errore recap"
            description={error}
          />
        )}

        {data && !loading && !error && (
          <>
            {/* Hero */}
            <header className="text-center mb-12 pb-10 border-b border-line" data-testid="recap-hero">
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-accent mb-4">
                Il tuo {year}
              </div>
              <h1 className="font-display tracking-tight text-6xl md:text-7xl mb-4">
                {year}
              </h1>
              {data.anno_meta?.tema && (
                <p className="font-display tracking-tight text-2xl md:text-3xl text-ink mb-3">
                  &ldquo;{data.anno_meta.tema}&rdquo;
                </p>
              )}
              {data.anno_meta?.perche && (
                <p className="font-body text-base text-soft max-w-xl mx-auto leading-relaxed">
                  {data.anno_meta.perche}
                </p>
              )}
            </header>

            {/* Coach quote */}
            <section className="mb-12" data-testid="recap-quote">
              <div className="flex items-start gap-4 p-6 md:p-8 border border-ink bg-[#FAFAF7] dark:bg-[#0F0F0F]">
                <Quote size={20} strokeWidth={1.5} className="text-accent shrink-0 mt-1" />
                <p className="font-display text-lg md:text-xl tracking-tight leading-relaxed text-ink whitespace-pre-line">
                  {data.quote}
                </p>
              </div>
              <p className="text-right mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
                — Coach Quantum
              </p>
            </section>

            {/* Stat cards */}
            <section className="grid grid-cols-2 gap-3 mb-12" data-testid="recap-stats">
              <BigStat
                label="Giorni con MIT"
                value={data.mit.days_with_mit}
                suffix={`/ ${data.mit.days_tracked}`}
                hint={`${ratioPct}% dei giorni tracciati`}
              />
              <BigStat
                label="MIT chiuse"
                value={data.mit.done}
                suffix={`/ ${data.mit.total}`}
                hint="Totale annuale"
                icon={Target}
              />
              <BigStat
                label="Streak record"
                value={data.streak.best}
                suffix="gg"
                hint={data.streak.best_end_date ? `fino al ${data.streak.best_end_date}` : '—'}
                icon={Flame}
                accent
              />
              <BigStat
                label="Mese top"
                value={data.best_month?.label || '—'}
                hint={data.best_month?.count ? `${data.best_month.count} MIT chiuse` : 'Nessuna MIT chiusa'}
                icon={Trophy}
              />
            </section>

            {/* Top KR */}
            <section className="mb-12" data-testid="recap-top-kr">
              <div className="mono-label mb-4 flex items-center gap-2">
                <Target size={12} strokeWidth={1.5} /> Top KR
              </div>
              {data.top_kr.length === 0 ? (
                <div className="font-body text-sm text-soft italic border border-dashed border-line p-6 text-center">
                  Nessun KR registrato per il {year}.
                </div>
              ) : (
                <ol className="space-y-4">
                  {data.top_kr.map((kr, idx) => {
                    const pct = Math.round((kr.progresso || 0) * 100);
                    return (
                      <li key={kr.id} className="grid grid-cols-[28px_1fr_auto] items-baseline gap-3 pb-3 border-b border-linesoft" data-testid={`recap-kr-${idx}`}>
                        <span className="font-display text-3xl text-mute tabular-nums">{idx + 1}</span>
                        <div className="min-w-0">
                          <div className="font-display text-lg tracking-tight truncate">{kr.titolo}</div>
                          {kr.obiettivo && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute truncate">
                              {kr.area ? `${kr.area} · ` : ''}{kr.obiettivo}
                            </div>
                          )}
                          <div className="mt-2 h-1.5 bg-linesoft" aria-hidden>
                            <div
                              className="h-full bg-ink"
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                        </div>
                        <div className="font-mono text-sm tabular-nums text-ink">{pct}%</div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            {/* CTA */}
            <section className="flex flex-wrap gap-3 justify-center pt-6 border-t border-line">
              <Link
                to={`/heatmap?anno=${year}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-ink text-ink hover:bg-ink hover:text-canvas font-mono text-xs uppercase tracking-[0.18em] transition-colors"
                data-testid="recap-open-heatmap"
              >
                <CalendarHeart size={14} strokeWidth={1.5} /> Vedi heatmap {year}
              </Link>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-line text-soft hover:border-ink hover:text-ink font-mono text-xs uppercase tracking-[0.18em] transition-colors"
                data-testid="recap-share-btn"
              >
                Copia link
              </button>
            </section>

            <p className="text-center mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
              Quantum Planner · quantumplanner.it
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function BigStat({ label, value, suffix, hint, icon: Icon, accent }) {
  return (
    <div className={'p-5 border ' + (accent ? 'border-ink bg-canvas' : 'border-line')}>
      <div className="mono-label flex items-center gap-1.5">
        {Icon ? <Icon size={12} strokeWidth={1.5} className={accent ? 'text-accent' : ''} /> : null}
        {label}
      </div>
      <div className="mt-2 font-display tracking-tight text-3xl md:text-4xl tabular-nums">
        {value} {suffix ? <span className="text-base text-mute font-body">{suffix}</span> : null}
      </div>
      {hint && (
        <div className="mt-1 font-body text-[12px] text-soft">{hint}</div>
      )}
    </div>
  );
}
